'use client';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useSetAtom } from 'jotai';
import { ReactNode, ReactChild, useCallback } from 'react';
import React from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { ShowNotification } from '~/app/(default-layout)/s/[storyId]/_components/chat-script-render/op';
import { maximumShownOpIndexAtom } from '~/app/(default-layout)/s/[storyId]/_state/chat-render-atoms';
import type { ChatScriptOperationWithIndex } from '~/app/_types/chat-script';
import { sleep } from '~/utils/sleep';
import { OperationAnimator } from './operation-animator';

type Props = {
  ops: ChatScriptOperationWithIndex[];
  children: ReactNode;
};

// Observe whether the elements of OperationItem are shown.
export const OperationViewableContainer = ({ ops, children }: Props) => {
  const setMaximumShownOpIndex = useSetAtom(maximumShownOpIndexAtom);
  const handleOnShown = useCallback(
    async (
      op: ChatScriptOperationWithIndex,
      containerElement: HTMLDivElement | null
    ) => {
      setMaximumShownOpIndex((prev) => Math.max(prev, Number(op.index)));

      if (op.operationType === 'show_notification_op') {
        // 通知を表示する
        toast.custom(
          (t) => {
            return (
              <ShowNotification
                op={op}
                onClick={() => toast.dismiss(t.id)}
                animation={
                  t.visible
                    ? 'enter 0.2s ease-out'
                    : 'leave 0.2s forwards ease-out'
                }
              />
            );
          },
          {
            duration: 4000,
            position: 'top-center',
            style: {
              width: '100%',
              padding: 0,
              border: 'none',
              color: 'transparent',
            },
          }
        );
      } else if (op.operationType === 'typing_now_op') {
        if (!document.body) return;
        // 入力中のハンドリング
        disableBodyScroll(document.body);
        await sleep(op.duration * 1000);
        enableBodyScroll(document.body);
        // hide element
        containerElement?.style.setProperty('display', 'none');
      }
    },
    [setMaximumShownOpIndex]
  );

  return (
    <>
      {React.Children.toArray(children)
        .filter(isReactChild)
        .map((child, index) => {
          const operation = ops[index];
          if (!operation) return child;

          return (
            <OperationAnimator
              operation={operation}
              key={`child-${index}`}
              onShown={handleOnShown}
            >
              {child}
            </OperationAnimator>
          );
        })}
      <Toaster />
    </>
  );
};

const isReactChild = (node: ReactNode): node is ReactChild => {
  return (
    typeof node === 'string' ||
    typeof node === 'number' ||
    React.isValidElement(node)
  );
};
